// skipcq: JS-W1028
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  collection,
  query,
  orderBy as firestoreOrderBy,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import FailureTableRow from './components/FailureTableRow';
import StatusFilterChip from './components/StatusFilterChip';

const CrawlerFailuresPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to get initial state from URL params
  const getInitialState = () => {
    const page = parseInt(searchParams.get('page')) || 0;
    const rowsPerPage = parseInt(searchParams.get('rowsPerPage')) || 10;
    const orderBy = searchParams.get('orderBy') || 'timestamp';
    const order = searchParams.get('order') || 'desc';
    const errorLocationFilter = searchParams.get('location') || 'All';

    // Parse status filters from URL
    const statusFilters = {
      pending: searchParams.get('pending') !== 'false',
      investigated: searchParams.get('investigated') !== 'false',
      resolved: searchParams.get('resolved') !== 'false',
    };

    return {
      page,
      rowsPerPage,
      orderBy,
      order,
      statusFilters,
      errorLocationFilter,
    };
  };

  const initialState = getInitialState();
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialState.page);
  const [rowsPerPage, setRowsPerPage] = useState(initialState.rowsPerPage);
  const [orderBy, setOrderBy] = useState(initialState.orderBy);
  const [order, setOrder] = useState(initialState.order);
  const [statusFilters, setStatusFilters] = useState(
    initialState.statusFilters,
  );
  const [errorLocationFilter, setErrorLocationFilter] = useState(
    initialState.errorLocationFilter,
  );

  // Function to update URL parameters
  const updateUrlParams = useCallback(
    (updates) => {
      const currentParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          currentParams.delete(key);
        } else {
          currentParams.set(key, value.toString());
        }
      });

      setSearchParams(currentParams);
    },
    [searchParams, setSearchParams],
  );

  const fetchFailures = useCallback(async () => {
    try {
      console.log('fetching failures');
      console.log(db);
      const failuresRef = collection(db, 'crawlerFailures');
      const q = query(failuresRef, firestoreOrderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);

      const failuresData = querySnapshot.docs.map((fdoc) => ({
        id: fdoc.id,
        ...fdoc.data(),
        timestamp: fdoc.data().timestamp?.toDate() || new Date(),
        lastRetry: fdoc.data().lastRetry?.toDate() || null,
      }));

      setFailures(failuresData);
    } catch (error) {
      console.error('Error fetching failures:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFailures();
  }, [fetchFailures]);

  // Update URL with current state on initial load if not already present
  useEffect(() => {
    const hasParams = searchParams.toString().length > 0;
    if (!hasParams) {
      updateUrlParams({
        page,
        rowsPerPage,
        orderBy,
        order,
        pending: statusFilters.pending,
        investigated: statusFilters.investigated,
        resolved: statusFilters.resolved,
        location: errorLocationFilter,
      });
    }
  }, []); // Run only once on mount

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'investigated':
        return 'info';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  }, []);

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPage(newPage);
      updateUrlParams({ page: newPage });
    },
    [updateUrlParams],
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setPage(0);
      updateUrlParams({
        rowsPerPage: newRowsPerPage,
        page: 0,
      });
    },
    [updateUrlParams],
  );

  const handleSort = useCallback(
    (property) => {
      // Compute the new order direction first
      const isCurrentlyAsc = orderBy === property && order === 'asc';
      const newOrder = isCurrentlyAsc ? 'desc' : 'asc';
      const isNewOrderAsc = newOrder === 'asc';

      setOrder(newOrder);
      setOrderBy(property);
      updateUrlParams({
        orderBy: property,
        order: newOrder,
      });

      const sortedFailures = [...failures].sort((a, b) => {
        if (property === 'timestamp') {
          return isNewOrderAsc
            ? a.timestamp - b.timestamp
            : b.timestamp - a.timestamp;
        }
        return isNewOrderAsc
          ? String(a[property]).localeCompare(String(b[property]))
          : String(b[property]).localeCompare(String(a[property]));
      });

      setFailures(sortedFailures);
    },
    [orderBy, order, updateUrlParams, failures],
  );

  const handleStatusFilterToggle = useCallback(
    (status) => {
      const newStatusFilters = {
        ...statusFilters,
        [status]: !statusFilters[status],
      };
      setStatusFilters(newStatusFilters);
      setPage(0); // Reset to first page when filter changes

      updateUrlParams({
        page: 0,
        pending: newStatusFilters.pending,
        investigated: newStatusFilters.investigated,
        resolved: newStatusFilters.resolved,
      });
    },
    [statusFilters, updateUrlParams],
  );

  const handleErrorLocationFilterChange = useCallback(
    (event) => {
      const newLocationFilter = event.target.value;
      setErrorLocationFilter(newLocationFilter);
      setPage(0); // Reset to first page when filter changes

      updateUrlParams({
        page: 0,
        location: newLocationFilter,
      });
    },
    [updateUrlParams],
  );

  const filteredFailures = failures.filter((failure) => {
    const statusMatch = statusFilters[failure.status];
    const locationMatch =
      errorLocationFilter === 'All' ||
      failure.errorLocation === errorLocationFilter;
    return statusMatch && locationMatch;
  });

  // Calculate displayed rows for current page
  const displayedRows = filteredFailures.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const emptyRows = Math.max(0, rowsPerPage - displayedRows.length);

  // Memoized callback functions to avoid recreating on every render
  const handleStatusFilterClick = useCallback(
    (status) => {
      handleStatusFilterToggle(status);
    },
    [handleStatusFilterToggle],
  );

  const handleSortClick = useCallback(
    (property) => {
      handleSort(property);
    },
    [orderBy, order, failures],
  );

  const handleViewFullDetailsClick = useCallback(
    (failureId) => {
      // Get the filtered failure IDs in order
      const filteredFailureIds = filteredFailures.map((f) => f.id);
      const currentIndex = filteredFailureIds.indexOf(failureId);

      navigate(`/failures/${failureId}`, {
        state: {
          filteredFailureIds,
          currentIndex,
          // Pass current filter state to maintain context
          filters: {
            statusFilters,
            errorLocationFilter,
            orderBy,
            order,
          },
        },
      });
    },
    [
      navigate,
      filteredFailures,
      statusFilters,
      errorLocationFilter,
      orderBy,
      order,
    ],
  );

  // Optimized sort handlers for each column
  const handleTimestampSort = useCallback(
    () => handleSortClick('timestamp'),
    [handleSortClick],
  );
  const handleBreweryNameSort = useCallback(
    () => handleSortClick('breweryName'),
    [handleSortClick],
  );
  const handleErrorTypeSort = useCallback(
    () => handleSortClick('errorType'),
    [handleSortClick],
  );
  const handleErrorLocationSort = useCallback(
    () => handleSortClick('errorLocation'),
    [handleSortClick],
  );
  const handleStatusSort = useCallback(
    () => handleSortClick('status'),
    [handleSortClick],
  );

  return (
    <DashboardLayout title="Data Issues">
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh"
          }}>
          <CircularProgress />
        </Box>
      ) : (
        // skipcq: JS-0415
        (<Box sx={{ height: '100%', width: '95%', p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Data Issues
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Filter by Status:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(statusFilters).map(([status, isSelected]) => (
                  <StatusFilterChip
                    key={status}
                    status={status}
                    isSelected={isSelected}
                    getStatusColor={getStatusColor}
                    onStatusClick={handleStatusFilterClick}
                  />
                ))}
              </Box>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Error Location</InputLabel>
                <Select
                  value={errorLocationFilter}
                  label="Error Location"
                  onChange={handleErrorLocationFilterChange}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="refreshBreweriesPlacesV2">
                    refreshBreweriesPlacesV2
                  </MenuItem>
                  <MenuItem value="crawler_request">crawler_request</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Paper sx={{ width: '100%', mb: 2, mr: 2, ml: 2 }}>
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table aria-labelledby="tableTitle">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'timestamp'}
                        direction={orderBy === 'timestamp' ? order : 'asc'}
                        onClick={handleTimestampSort}
                      >
                        Timestamp
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'breweryName'}
                        direction={orderBy === 'breweryName' ? order : 'asc'}
                        onClick={handleBreweryNameSort}
                      >
                        Brewery
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'errorType'}
                        direction={orderBy === 'errorType' ? order : 'asc'}
                        onClick={handleErrorTypeSort}
                      >
                        Error Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'errorLocation'}
                        direction={orderBy === 'errorLocation' ? order : 'asc'}
                        onClick={handleErrorLocationSort}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={handleStatusSort}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRows.map((failure) => (
                    <FailureTableRow
                      key={failure.id}
                      failure={failure}
                      getStatusColor={getStatusColor}
                      onViewFullDetails={handleViewFullDetailsClick}
                    />
                  ))}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 53 * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredFailures.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>)
      )}
    </DashboardLayout>
  );
};

export default CrawlerFailuresPage;
